import {TreeGroupOptionItem} from './../original-models/tree-item.model';
import {Injectable, OnDestroy} from '@angular/core';
import {GroupService} from './group.service';
import {StorageService} from './storage.service';
import {CompanyService} from './company.service';
import {LoggerService} from './logger.service';
import {BehaviorSubject, Observable, Subject, Subscription} from 'rxjs';
import {filter, take, takeUntil} from 'rxjs/operators';
import {LevelType, SelectedLevel} from 'app/original-models/level-type.model';
import {AuthenticationService} from './authentication.service';
import {IntercomSettings} from 'app/original-models/intercom-settings.model';
import {ACCESS} from 'app/original-models/access.model';
import {CompanyTreeService} from 'app/services/company/company-tree.service';
import { UtilsService } from 'app/services/utils/utils.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class CompanyGroupService implements OnDestroy {
    groupsSubscription: Subscription;
    companiesSubscription: Subscription;
    private _treeSubject = new BehaviorSubject<TreeGroupOptionItem[]>(null);
    readonly tree$ = this._treeSubject.asObservable();
    public companiesListChanged$ = new Subject<void>();
    companies: any[];
    groups: any[];
    initTree: boolean;
    private _initialized = false;
    private readonly destroy$ = new Subject<void>();
    constructor(
        private storage: StorageService,
        private companyService: CompanyService,
        private groupService: GroupService,
        private authService: AuthenticationService,
        private companyTreeService: CompanyTreeService,
        private logger: LoggerService,
        public utilsService: UtilsService,
        private translate: TranslateService) {
            this.authService.afterLogin$.pipe(takeUntil(this.destroy$)).subscribe(() => {
                this._treeSubject.next(null);
                this._initialized = false;
                this.companies = null;
                this.getTree(true);
            });
            this.authService.beforeLogout$.pipe(takeUntil(this.destroy$)).subscribe(() => {
                this._treeSubject.next(null);
                this._initialized = false;
                this.companies = null;
            });
        }

        ngOnDestroy(): void {
            this.destroy$.next();
            this.destroy$.complete();
        }

        getTreeSubscription(refresh: boolean) {
            let tree = [];
            if (this.groupsSubscription) {
                this.groupsSubscription.unsubscribe();
            }
            this.groupsSubscription = this.groupService
                .getGroups()
                .pipe(filter(g => !!g), take(1))
                .subscribe((groupsData) => {
                        if (groupsData) {
                            tree = [];
                            let ownGroupOptions = {title: 'Header.MyCompanies', subcategories: []};
                            let invitedGroupOptions = {title: 'Header.InvitedCompanies', subcategories: []};
                            if (groupsData.length > 0) {
                                let groupTree = groupsData.map((item) => {
                                    return this.companyTreeService.mapTreeItem(item, LevelType.Group, item.isOwner(this.storage.userId, this.storage.model));
                                });

                                this.companyService.getCompanies().pipe(filter(d => !!d), take(1)).subscribe(data => {
                                    if (data) {
                                        if (data.length > 0) {
                                            let filteredTree = JSON.parse(JSON.stringify(groupTree));
                                            this.companyTreeService.filterByRole(filteredTree);
                                            let companies = data.filter((item) => {
                                                return item.isActive;
                                            }).filter((item) => {
                                                let exists = this.companyTreeService.getSelected(filteredTree, new SelectedLevel(item.id, LevelType.Company), null);
                                                //exclude company - w\a to not show company with group
                                                return (exists === null || exists === undefined) && item.currentUserRole !== ACCESS.GroupReviewer;
                                            }).map(item => {
                                                return this.companyTreeService.mapTreeItem(item, LevelType.Company, item.isOwner(this.storage.userId, this.storage.model));
                                            });

                                            let ownerCompanyIndex = companies.findIndex(item => item.isOwner);
                                            let ownerGroupIndex = groupTree.findIndex(item => item.isOwner);

                                            //ownerGroup
                                            groupTree.filter((item) => {
                                                return item.isOwner;
                                            }).forEach((item) => {
                                                ownGroupOptions.subcategories.push(item);
                                            });

                                            if (!this.storage.selectedLevel || !this.storage.selectedLevel.id) {
                                                if (ownerGroupIndex >= 0 || ownerCompanyIndex < 0) {
                                                    let groupId = ownerGroupIndex >= 0 ? groupTree[ownerGroupIndex].id : groupTree[0].id;
                                                    this.storage.selectedLevel = new SelectedLevel(groupId, LevelType.Group);
                                                } else {
                                                    this.companyService.setCurrentCompany(data);
                                                }
                                            }
                                            //owner companies
                                            companies.filter((item) => {
                                                return item.isOwner;
                                            }).sort((a, b) => a.order - b.order)
                                            .forEach((company) => {
                                                ownGroupOptions.subcategories.push(company);
                                            });

                                            //invited groups
                                            groupTree.filter((item) => {
                                                return !item.isOwner;
                                            }).sort(this.utilsService.sortByTitleCaseInsensitive).forEach((item) => {
                                                invitedGroupOptions.subcategories.push(item);
                                            });

                                            //invited companies
                                            companies.filter((item) => {
                                                return !item.isOwner;
                                            }).sort(this.utilsService.sortByTitleCaseInsensitive).forEach((company) => {
                                                invitedGroupOptions.subcategories.push(company);
                                            });

                                            if(ownGroupOptions.subcategories.length) {
                                                tree.push(ownGroupOptions);
                                            }
                                            if(invitedGroupOptions.subcategories.length) {
                                                tree.push(invitedGroupOptions);
                                            }
                                            this._treeSubject.next(tree);
                                        } else {
                                            groupTree.filter((item) => {
                                                return item.isOwner;
                                            }).forEach((item) => {
                                                ownGroupOptions.subcategories.push(item);
                                            });
                                            groupTree.filter((item) => {
                                                return !item.isOwner;
                                            }).sort(this.utilsService.sortByTitleCaseInsensitive).forEach((item) => {
                                                invitedGroupOptions.subcategories.push(item);
                                            });


                                            if(ownGroupOptions.subcategories.length) {
                                                tree.push(ownGroupOptions);
                                            }
                                            if(invitedGroupOptions.subcategories.length) {
                                                tree.push(invitedGroupOptions);
                                            }
                                            this._treeSubject.next(tree);
                                            if (!this.storage.selectedLevel || !this.storage.selectedLevel.id) {
                                                this.storage.selectedLevel = new SelectedLevel(tree[0].id, LevelType.Group);
                                            }
                                        }
                                    }
                                }, error => {
                                    this.logger.error(error);
                                    this._treeSubject.next(tree);
                                });
                            }
                            else {
                                tree = []
                                if (!this.companies) {
                                    this.initTree = true;
                                    this.getCompanyTree(refresh);
                                } else {
                                    this._treeSubject.next(this.companies);
                                }
                            }
                        }
                    }, error => {
                        tree = [];
                        if (!this.companies) {
                            this.initTree = true;
                            this.getCompanyTree(refresh);
                        } else {
                            this._treeSubject.next(this.companies);
                        }
                    }
                );
        }

        getCompanyTree(refresh: boolean) {
            let tree = [];
            let ownGroupOptions = {title: this.translate.instant('Header.MyCompanies'), subcategories: []};
            let invitedGroupOptions = {title: this.translate.instant('Header.InvitedCompanies'), subcategories: []};
            this.companies = null;
            if (this.companiesSubscription) {
                this.companiesSubscription.unsubscribe();
            }
            this.companiesSubscription = this.companyService.getCompanies().pipe(filter(d => !!d), take(1)).subscribe(data => {
                let tree = [];
                this.companies = null;
                if (data) {
                    this.companies = [];
                    if (refresh && (!this.storage.selectedLevel || !this.storage.selectedLevel.id)) {
                        this.companyService.setCurrentCompany(data);
                    }
                    if (data.length > 0) {
                        data.filter((item) => {
                            return item.isActive && item.isOwner(this.storage.userId, this.storage.model);
                        }).sort((a, b) => a.order - b.order)
                        .forEach((company) => {
                            ownGroupOptions.subcategories.push(this.companyTreeService.mapTreeItem(company, LevelType.Company, true));
                        });
                        data.filter((item) => {
                            return item.isActive && !item.isOwner(this.storage.userId, this.storage.model);
                        }).sort(this.utilsService.sortByNameCaseInsensitive).forEach((company) => {
                            invitedGroupOptions.subcategories.push(this.companyTreeService.mapTreeItem(company, LevelType.Company));
                        });
                        if(ownGroupOptions.subcategories.length) {
                            tree.push(ownGroupOptions);
                        }
                        if(invitedGroupOptions.subcategories.length) {
                            tree.push(invitedGroupOptions);
                        }
                        this.companies = tree;
                        this._treeSubject.next(tree);
                        this.initTree = false;
                    } else {
                        this._treeSubject.next(this.companies);
                    }
                }
            }, error => {
                this.logger.error(error);
                tree = [];
                if (this.initTree) {
                    this._treeSubject.next(tree);
                }
            });
        }

        resetTree(refresh: boolean = false) {
            this._treeSubject.next(null);
            this.companies = null;
            this._initialized = false;
            this.getTree(refresh);
        }

        getTree(refresh: boolean): Observable<TreeGroupOptionItem[]> {
            if (!this._initialized || refresh) {
                this._initialized = true;
                this.getTreeSubscription(refresh);
            }
            return this.tree$;
        }

        getCurrent(refresh?: boolean): Observable<any> {
            if (!this.storage.selectedLevel || this.storage.selectedLevel.type === LevelType.Company) {
                return this.companyService.getCurrentCompany(refresh);
            }
            else {
                return this.groupService.getCurrentGroup(this.storage.selectedLevel.id, refresh);
            }
        }

        getIntercomSettings(): Observable<IntercomSettings> {
            if (!this.storage.selectedLevel || this.storage.selectedLevel.type === LevelType.Company) {
                if (this.storage.companyId) {
                    return this.companyService.getIntercomSettings();
                }
            }
            else {
                let role = this.storage.model && this.storage.model.length > 1 ? this.storage.model.charAt(0).toUpperCase() + this.storage.model.slice(1).toLowerCase() : '';
                return this.groupService.getIntercomSettings(this.storage.selectedLevel.id, `${role}${this.storage.userId}`);
            }
        }


}
